import { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';

import { distances, colors } from '../styles/constants';
import { H1 } from './Typography';
import Icon from './Icons';
import { useEnvFrame } from '../auth/accessToken/EnvFrame';

interface PageProps {
    hasEnvFrame?: boolean;
    children: ReactNode;
    title: ReactNode;
}

const Page = (props: PageProps) => {
    const TitleWrapper = typeof props.title === 'string' ? AlignedTitle : AlignedDiv;
    const hasEnvFrame = !!useEnvFrame();
    return (
        <PageWrapper hasEnvFrame={props.hasEnvFrame ?? hasEnvFrame ?? false}>
            <TitleWrapper>{props.title}</TitleWrapper>
            {props.children}
        </PageWrapper>
    );
};

interface PageWrapperProps {
    hasEnvFrame: boolean;
}

const PageWrapper = styled.section<PageWrapperProps>`
    flex: 1;
    box-sizing: border-box;
    ${(props) =>
        props.hasEnvFrame
            ? `
        padding: 4px calc(${distances.normal});

    `
            : `
        padding: 44px calc(${distances.normal} + 4px);
    `};

    @media (max-width: 768px) {
        padding: 4px ${distances.tiny};
        width: 100%;
    }
`;

const HeaderLink = styled.span`
    display: inline-block;
    position: relative;
    bottom: 5px;
    cursor: pointer;
    margin-right: ${distances.small};

    path {
        fill: ${colors.primary};
    }

    &:hover {
        path {
            fill: ${colors.primaryHover};
        }
    }
`;

const backFrames = keyframes`
  from {
    left: 0;
  }

  to {
    left: -10px;
  }
`;

const HeaderBackLink = styled(HeaderLink)`
    &:hover {
        animation: ${backFrames} 1000ms ease-out infinite;
        animation-delay: 2s;
    }
`;

const dismissFrames = keyframes`
  from {
    transform: scale(0.5);
  }

  to {
    transform: scale(1);
  }
`;

const HeaderDismissLink = styled(HeaderLink)`
    &:hover {
        animation: ${dismissFrames} 1000ms ease-out infinite;
        animation-delay: 2s;
    }
`;

const _Back = ({ to, history }: { to?: string } & RouteComponentProps) => (
    <HeaderBackLink onClick={() => to ? history.push(to) : history.goBack()}>
        <Icon icon="arrow_left" />
    </HeaderBackLink>
);
export const Back = withRouter(_Back);

interface CustomBackProps {
    onClick?: ((event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void) | undefined;
}
export const CustomBack = (props: CustomBackProps) => (
    <HeaderBackLink onClick={props.onClick}>
        <Icon icon="arrow_left" />
    </HeaderBackLink>
);

export const Title = styled(H1)`
    color: ${colors.text};
    display: inline-block;
`;

const AlignedTitle = styled(Title)`
    padding-left: ${distances.normal};
    padding-right: ${distances.normal};
    padding-bottom: calc(${distances.small} + ${distances.tiny});
    max-width: 1200px;

    @media (max-width: 768px) {
        padding-left: ${distances.small};
        padding-right: ${distances.small};
        padding-top: ${distances.small};
    }
`;

const AlignedDiv = styled.div`
    padding-left: ${distances.normal};
    padding-right: ${distances.normal};
    padding-bottom: calc(${distances.small} + ${distances.tiny});
    max-width: 1200px;

    @media (max-width: 768px) {
        display: flex;
        flex-flow: column nowrap;
        padding-top: ${distances.small};
        padding-left: ${distances.tiny};
        padding-right: ${distances.tiny};
    }
`;

interface DismissProps extends RouteComponentProps {
    handleClick?: () => void;
}

export const Dismiss = withRouter<DismissProps, React.FC<DismissProps>>(({ history, handleClick }) => {
    const onClick = handleClick ? handleClick : () => history.goBack();
    return (
        <HeaderDismissLink onClick={onClick}>
            <Icon icon="close" />
        </HeaderDismissLink>
    );
});

export const PageButton = styled.button`
    border: none;
    background: transparent;
    outline: none;
    float: right;
    color: ${colors.primary};
    padding-top: 13px;
    font-weight: bold;
    cursor: pointer;

    svg {
        position: relative;
        top: -2px;
    }

    &:hover:enabled {
        color: ${colors.primaryHover};
        path {
            fill: ${colors.primaryHover};
        }
    }

    &:disabled {
        opacity: 0.3;
    }

    @media (max-width: 768px) {
        float: left;
        margin-right: auto;
    }
`;

export const PageButtonLink = styled(Link)`
    border: none;
    background: transparent;
    outline: none;
    float: right;
    color: ${colors.primary};
    padding-top: 13px;
    font-weight: bold;
    cursor: pointer;
    text-decoration: none;

    svg {
        position: relative;
        top: -2px;
    }

    &:hover {
        color: ${colors.primaryHover};
        path {
            fill: ${colors.primaryHover};
        }
    }

    &.disabled {
        opacity: 0.3;
        pointer-events: none;
        cursor: not-allowed;
    }
`;

export const PageButtonExternalLink = styled.a`
    border: none;
    background: transparent;
    outline: none;
    float: right;
    color: ${colors.primary};
    padding-top: 13px;
    font-weight: bold;
    cursor: pointer;
    text-decoration: none;

    svg {
        position: relative;
        top: -2px;
    }

    &:hover {
        color: ${colors.primaryHover};
        path {
            fill: ${colors.primaryHover};
        }
    }

    &.disabled {
        opacity: 0.3;
        pointer-events: none;
        cursor: not-allowed;
    }
`;

export default Page;
